import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { Box, Tabs, Tab } from '@mui/material';

/*
 * Two-level page navigation for detail pages.
 *
 * Row 1 = section groups (the existing grey tab bar). Row 2 = the pages inside
 * the selected group, shown only when that group has 2+ visible pages.
 *
 * URLs stay flat: `value` / `onChange` always use the LEAF page value
 * (e.g. 'market', 'codes'), so existing deep links keep working. The group is
 * derived from whichever section contains the leaf.
 *
 * sections: [
 *   { value: 'details', label: 'Details & Contacts' },          // no children = a page by itself
 *   { value: 'market-data', label: 'Market Data', visible: hasClaims, children: [
 *     { value: 'market', label: 'Overview' },
 *     { value: 'trends', label: 'Trends', visible: isAdmin },
 *   ] },
 * ]
 *
 * Rules: groups/pages with `visible: false` are hidden; a group with no visible
 * pages disappears; a group with one visible page shows no second row. Keep it
 * to two levels — anything deeper belongs inside the content (e.g. a
 * ToggleButtonGroup), not a third tab row.
 */

export const getVisibleSections = (sections) => sections
  .filter((s) => s.visible !== false)
  .map((s) => ({
    ...s,
    children: (s.children || [{ value: s.value, label: s.label }])
      .filter((c) => c.visible !== false),
  }))
  .filter((s) => s.children.length > 0);

export const findSection = (sections, leafValue) => getVisibleSections(sections)
  .find((s) => s.children.some((c) => c.value === leafValue));

const secondaryTabSx = {
  minHeight: 40,
  minWidth: 0,
  px: 0,
  py: 0,
  mr: 3,
  // Font, size, weight, uppercase and colours (grey, blue hover/selected) all
  // come from the theme's MuiTab overrides, so this row matches the group row.
  '&.Mui-focusVisible': {
    outline: '2px solid #2082F4',
    outlineOffset: -2,
  },
};

export function SectionTabs({ sections, value, onChange }) {
  // Remembers the last page opened in each group so returning to a group
  // lands where the user left off.
  const [lastVisited, setLastVisited] = useState({});

  const visible = getVisibleSections(sections);
  const active = visible.find((s) => s.children.some((c) => c.value === value));
  const showSecondary = active && active.children.length > 1;

  const goTo = (groupValue, leafValue) => {
    setLastVisited((prev) => ({ ...prev, [groupValue]: leafValue }));
    if (leafValue !== value) {
      onChange(leafValue);
    }
  };

  const onGroupChange = (event, groupValue) => {
    const group = visible.find((s) => s.value === groupValue);
    if (!group) return;
    const remembered = lastVisited[groupValue];
    const target = group.children.some((c) => c.value === remembered)
      ? remembered
      : group.children[0].value;
    goTo(groupValue, target);
  };

  return (
    <nav aria-label='Page sections'>
      <Tabs
        variant='scrollable'
        scrollButtons={false}
        value={active ? active.value : false}
        onChange={onGroupChange}
        aria-label='Section groups'
        sx={{ pl: 3, backgroundColor: 'tertiary.main' }}
      >
        {visible.map((s) => (
          <Tab key={s.value} label={s.label} value={s.value} disableRipple />
        ))}
      </Tabs>
      {showSecondary && (
        <Box
          sx={{
            // Row 1 has 24px left padding + MUI Tab's 16px inner padding, so 40px
            // lines the first sub-tab label up with the first group label.
            pl: 5,
            pr: 3,
            // Halfway between the group row (#EDEDED) and the page (#FAFAFA).
            backgroundColor: '#F4F4F4',
            borderTop: '1px solid #DCDCDC',
          }}
        >
          <Tabs
            variant='scrollable'
            scrollButtons={false}
            value={value}
            onChange={(event, leafValue) => goTo(active.value, leafValue)}
            aria-label={`${active.label} pages`}
            // 14px label (~17px line box) centred in a 40px tab ends ~11px above the
            // bottom; lifting the underline 7px leaves ~3px between word and line.
            // Underline colour is the theme's blue (#2082F4).
            slotProps={{ indicator: { sx: { marginBottom: '7px' } } }}
            sx={{ minHeight: 40 }}
          >
            {active.children.map((c) => (
              <Tab
                key={c.value}
                label={c.label}
                value={c.value}
                disableRipple
                sx={secondaryTabSx}
              />
            ))}
          </Tabs>
        </Box>
      )}
    </nav>
  );
}

const pageShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  visible: PropTypes.bool,
});

SectionTabs.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    visible: PropTypes.bool,
    children: PropTypes.arrayOf(pageShape),
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SectionTabs;
